<script setup lang="ts">
definePageMeta({ prerender: true })

const { data: home } = await useAsyncData('home', () => queryCollection('home').first())
const { data: games } = await useAsyncData('games', () => queryCollection('games').first())
const { trackEvent } = useAnalytics()

function handleStripTileClick(slug: string) {
  trackEvent('library_tile_clicked', { game: slug, source: 'home_strip' })
}

useSeoMeta({
  description: () => home.value?.seo.description,
  keywords: () => home.value?.seo.keywords?.join(', '),
  ogDescription: () => home.value?.seo.ogDescription ?? home.value?.seo.description,
  ogTitle: () => home.value?.seo.ogTitle ?? home.value?.seo.title,
  title: () => home.value?.seo.title,
  twitterDescription: () => home.value?.seo.ogDescription ?? home.value?.seo.description,
  twitterTitle: () => home.value?.seo.ogTitle ?? home.value?.seo.title,
})

// The OG image renders the brand name as its visual title. `og:title`
// meta carries the descriptive headline; the image title stays short
// so it reads cleanly at 1200×630.
defineOgImageComponent('Default', {
  description: () => home.value?.seo.ogDescription ?? home.value?.seo.description ?? '',
  eyebrow: 'signalfire.games',
  title: 'Signal Fire Games',
})

// dateModified is emitted only when the content YAML carries a real
// lastUpdated — keeping the date honest per Mueller's guidance that
// "changing the date without doing anything else is just noise."
if (home.value?.lastUpdated) {
  useSchemaOrg([ defineWebPage({ dateModified: home.value.lastUpdated }) ])
}

const availableGames = computed(() =>
  (games.value?.games ?? [])
    .filter(g => g.status === 'available')
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
)

const comingSoonGames = computed(() =>
  (games.value?.games ?? [])
    .filter(g => g.status === 'coming-soon')
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99)),
)

const stripGames = computed(() => {
  const max = home.value?.gamesStrip.maxShown ?? 3
  const live = availableGames.value.slice(0, max)
  const remaining = max - live.length
  return remaining > 0 ? [ ...live, ...comingSoonGames.value.slice(0, remaining) ] : live
})

// Tilt cadence for repeating groups. 4 leans left, 2 nearly flat, 3 leans
// right — keeps a row of stickers from marching in lockstep.
const STEP_TILTS = [ 4, 2, 3 ] as const
const TILE_TILTS = [ 1, 3, 4 ] as const
const tiltFor = (cycle: readonly (1 | 2 | 3 | 4)[], i: number) => cycle[i % cycle.length]!
</script>

<template>
  <div v-if="home">
    <!--
      Hero sticker composition.

      Text stickers used sparingly:
        - TEAM SHOWDOWN banner (focal point)
        - NO SIGN UP tag (accent, bottom-left)
      Everything else is a graphic shape sticker or the flame
      rosette. Shape stickers cluster tightly around the main
      sticker on the right side of the split hero, close to each
      other without overlapping. Left column stays unstickered so
      the headline reads cleanly.

      Layers:
        z-10 = right-column star + flame rosette + shape accents
        z-20 = main TEAM SHOWDOWN banner
        z-30 = NO SIGN UP tag

      Responsive visibility (applied per-sticker via Tailwind):
        XS           — main banner, NO SIGN UP, shrunk rosette only
        SM           — + upper-right star
        MD           — + sparkle + starburst (2 shape accents)
        LG and up    — full cluster, all 7 shape primitives
    -->
    <!-- ===== 1. Hero ===== -->
    <SfgSheet :padded="false">
      <div class="relative mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
        <div class="relative z-10 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div class="mb-6 flex">
              <SfgEyebrow variant="inverted">
                {{ home.hero.eyebrow }}
              </SfgEyebrow>
            </div>

            <SfgHeading
              :level="1"
              :highlight="home.hero.headline.highlight"
              highlight-color="yellow"
              class="text-brand-chalk"
              :style="{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.65)' }"
            >
              {{ home.hero.headline.prefix }}
              <template #after>
                {{ ' ' + home.hero.headline.suffix }}
              </template>
            </SfgHeading>

            <p class="font-body text-body mt-6 max-w-lg text-brand-chalk">
              {{ home.hero.description }}
            </p>

            <div class="flex flex-wrap gap-4 mt-8">
              <SfgButton
                variant="primary"
                size="md"
                :to="home.hero.primaryCta.to"
              >
                {{ home.hero.primaryCta.label }}
              </SfgButton>
              <SfgButton
                variant="ghost"
                size="md"
                :to="home.hero.secondaryCta.to"
              >
                {{ home.hero.secondaryCta.label }}
              </SfgButton>
            </div>
          </div>

          <div class="relative h-95 sm:h-110 lg:h-130 xl:h-150">
            <!--
              Hero sticker grid. All elements visible at every
              breakpoint — sizes scale via clamp() rather than
              responsive `hidden` classes.

              Aesthetic: shadow only, no borders. Shape stickers
              get .sfg-no-stroke on their wrappers, which strips
              SVG strokes so the shapes render as flat silhouettes.
              Text stickers (banner, NO SIGN UP) use SfgSticker's
              `flat` mode (no white die-cut, no dark border, just a
              chunky offset shadow). Every sticker reads as a flat
              color shape lifted by a single hard drop shadow.

              Conceptual 4×4 cell layout:
                Banner       (0-3, 0-1)   4 cols × 2 rows, top half
                Big star     (3, 0)       upper-right corner
                Flame mark   (3, 1-2)     right side; banner z-20
                                          partially covers the upper-
                                          left of the flame, peeks out
                Starburst    (0, 2)
                Heart        (1, 2)
                Lightning1   (2, 2)
                Lightning2   (1-2, 3)     spans 2 cols
                Second star  (3, 3)       bottom-right corner
                NO SIGN UP   (0-1, 3)     spans 2 cols × 1 row

              Per-cell offsets are pseudo-random (hand-picked so
              SSR/CSR match) — each shape lands a few percent off
              its grid center, with tilt also varied. Result: even
              distribution at the macro level, organic-looking
              collisions where neighbors clip each other.
            -->

            <!--
              Sticker size clamps use higher floors than before so
              shape stickers stay substantial on small viewports.
              TEAM SHOWDOWN banner is the exception — its scale at
              SM/XS is unchanged (still 1.10), so on small screens
              the banner stays the same size while every shape
              around it grows.
            -->

            <!-- Big yellow star — cell (3, 0), tucked corner -->
            <div class="absolute top-1 right-3 z-10 pointer-events-none sfg-no-stroke">
              <SfgStar
                size="clamp(95px, 7vw + 10px, 150px)"
                :tilt="4"
                fill="yellow"
              />
            </div>

            <!--
              Flame mark — cell (3, 1-2). :disk="false" gives the
              bare two-layer flame silhouette. Banner z-20 covers
              the upper-left, flame peeks below + right.
              tilt=5 (+10deg) leans the flame to the right.
              MD position pulled back up from 52% → 44%; the prior
              MD slide was too aggressive.
            -->
            <div class="absolute top-[40%] -right-[3%] md:top-[44%] md:-right-[5%] lg:top-[40%] lg:-right-[3%] z-10 pointer-events-none sfg-no-stroke">
              <SfgFlame
                size="clamp(130px, 11vw + 30px, 260px)"
                :disk="false"
                body-color="red"
                flame-color="yellow"
                :tilt="5"
              />
            </div>

            <!-- Starburst — cell (0, 2), upper-left of strip -->
            <div class="absolute top-[51%] left-[6%] z-10 pointer-events-none sfg-no-stroke">
              <SfgStarburst
                size="clamp(95px, 7vw + 35px, 130px)"
                fill="yellow"
                :tilt="7"
              />
            </div>

            <!-- Heart — cell (1, 2), nudged right -->
            <div class="absolute top-[63%] left-[30%] z-10 pointer-events-none sfg-no-stroke">
              <SfgHeart
                size="clamp(90px, 5vw + 45px, 120px)"
                fill="red"
                :tilt="-9"
              />
            </div>

            <!-- Lightning 1-zig — cell (2, 2), pushed down-right within cell -->
            <div class="absolute top-[58%] left-[60%] z-10 pointer-events-none sfg-no-stroke">
              <SfgLightning1
                size="clamp(95px, 7vw + 35px, 130px)"
                fill="chalk"
                :tilt="-13"
              />
            </div>

            <!--
              Lightning 2-zig (the triple-segment bolt).

              XS / SM / MD: anchored bottom-2 left-[44%]. Bolt sits
                low and pokes up from behind NO SIGN UP.
              LG / XL: pushed deeper below the column with bottom-[-30px]
                (was -15px) so most of the bolt's body sits below
                the visible column edge. The top portion peeks up
                between the cluster and the column floor.
            -->
            <div class="absolute left-[44%] bottom-2 lg:left-[50%] lg:-bottom-7.5 z-10 pointer-events-none sfg-no-stroke">
              <SfgLightning2
                size="clamp(110px, 8vw + 30px, 160px)"
                fill="red"
                :tilt="14"
              />
            </div>

            <!-- Second chalk star — cell (3, 3), bottom corner with slight outset -->
            <div class="absolute bottom-1 right-1 z-10 pointer-events-none sfg-no-stroke">
              <SfgStar
                size="clamp(75px, 4vw + 28px, 100px)"
                :tilt="1"
                fill="chalk"
              />
            </div>

            <!--
              Banner: original die-cut style. md:scale-100 dials it
              back at MD so the banner stops crowding the column at
              tablet widths and the flame can find its own footing
              to the right of the banner edge.
            -->
            <div class="absolute top-16 left-0 sm:left-4 z-20 scale-110 md:scale-100 lg:scale-[1.45] xl:scale-[1.6] origin-top-left">
              <SfgStickerBanner
                top-line="TEAM"
                bottom-line="SHOWDOWN"
                fill="yellow"
                :tilt="1"
              />
            </div>

            <!--
              NO SIGN UP: original die-cut style. Pulled all the
              way to the left at LG (left-2) so the bottom-row real
              estate splits cleanly: NO SIGN UP on the left, lightning
              2-zig poking up at the bottom-center.
            -->
            <div class="absolute bottom-6 left-8 sm:left-14 lg:left-2 z-30 scale-100 lg:scale-150 xl:scale-180 origin-bottom-left">
              <SfgStickerBar
                text="NO SIGN UP"
                fill="chalk"
                :tilt="4"
              />
            </div>
          </div>
        </div>
      </div>
    </SfgSheet>

    <!-- ===== 2. What this is ===== -->
    <SfgPaper>
      <div class="mx-auto max-w-3xl">
        <SfgEyebrow variant="outline">
          {{ home.whatThisIs.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="2" class="mt-5">
          {{ home.whatThisIs.heading }}
        </SfgHeading>
        <div class="flex flex-col gap-5 mt-8 font-body text-body">
          <p>{{ home.whatThisIs.body }}</p>
          <p>{{ home.whatThisIs.bodyCont }}</p>
        </div>
      </div>
    </SfgPaper>

    <!-- ===== 3. How it runs ===== -->
    <SfgSheet id="how-it-runs" :padded="false">
      <div class="mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
        <SfgEyebrow variant="inverted">
          {{ home.howItRuns.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="2" class="text-brand-chalk mt-5 mb-12">
          {{ home.howItRuns.heading }}
        </SfgHeading>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            v-for="(step, i) in home.howItRuns.steps"
            :key="step.number"
            class="flex flex-col"
          >
            <span class="font-display-alt text-[80px] leading-none text-brand-yellow">
              {{ step.number }}
            </span>
            <div class="mt-4 mb-4 inline-flex self-start">
              <SfgStickerBar
                :text="step.title"
                fill="chalk"
                :tilt="tiltFor(STEP_TILTS, i)"
              />
            </div>
            <p class="font-body text-body text-brand-chalk">
              {{ step.body }}
            </p>
          </div>
        </div>
      </div>
    </SfgSheet>

    <!-- ===== 3.5 Where it works ===== -->
    <SfgPaper :padded="false">
      <div class="mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
        <div class="max-w-3xl">
          <SfgEyebrow variant="outline">
            {{ home.whereItWorks.eyebrow }}
          </SfgEyebrow>
          <SfgHeading :level="2" class="mt-5">
            {{ home.whereItWorks.heading }}
          </SfgHeading>
          <p class="font-body text-body mt-6">
            {{ home.whereItWorks.intro }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          <div
            v-for="scenario in home.whereItWorks.scenarios"
            :key="scenario.title"
            class="flex flex-col"
          >
            <h3 class="font-display uppercase text-[14px] tracking-[0.14em] text-brand-ink">
              {{ scenario.title }}
            </h3>
            <p class="font-body text-body mt-3">
              {{ scenario.body }}
            </p>
          </div>
        </div>
      </div>
    </SfgPaper>

    <!-- ===== 4. Games strip ===== -->
    <SfgPaper :padded="false">
      <div class="mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
        <div class="flex items-end justify-between gap-4 flex-wrap mb-10">
          <div>
            <SfgEyebrow variant="outline">
              {{ home.gamesStrip.eyebrow }}
            </SfgEyebrow>
            <SfgHeading :level="2" class="mt-5">
              {{ home.gamesStrip.heading }}
            </SfgHeading>
          </div>
          <NuxtLink
            :to="home.gamesStrip.linkTo"
            class="font-display uppercase text-[13px] tracking-[0.14em] text-brand-ink hover:text-brand-red transition-colors"
          >
            {{ home.gamesStrip.linkLabel }} →
          </NuxtLink>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <template v-for="(game, i) in stripGames" :key="game.slug">
            <NuxtLink
              v-if="game.status === 'available' && game.route"
              :to="game.route"
              class="block rounded-card focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-ink"
              @click="handleStripTileClick(game.slug)"
            >
              <SfgGameTile
                :title="game.title"
                :format="game.format"
                :type="game.type"
                :duration="game.duration"
                :player-note="game.playerNote"
                status="live"
                :sticker-top="game.stickerTop"
                :sticker-bottom="game.stickerBottom"
                :sticker-tilt="tiltFor(TILE_TILTS, i)"
                :art-fill="game.artFill"
              />
            </NuxtLink>
            <SfgGameTile
              v-else
              :title="game.title"
              :format="game.format"
              :type="game.type"
              :duration="game.duration"
              :player-note="game.playerNote"
              status="soon"
              :sticker-top="game.stickerTop"
              :sticker-bottom="game.stickerBottom"
              :sticker-tilt="tiltFor(TILE_TILTS, i)"
              :art-fill="game.artFill"
            />
          </template>
        </div>
      </div>
    </SfgPaper>

    <!-- ===== 5. Closing CTA ===== -->
    <SfgSheet>
      <div class="mx-auto max-w-2xl text-center">
        <SfgEyebrow variant="inverted">
          {{ home.closing.eyebrow }}
        </SfgEyebrow>
        <SfgHeading :level="2" class="text-brand-chalk mt-5">
          {{ home.closing.heading }}
        </SfgHeading>
        <p class="font-body text-body mt-6 text-brand-chalk">
          {{ home.closing.body }}
        </p>
        <div class="mt-8 flex justify-center">
          <SfgButton
            variant="primary"
            size="md"
            :to="home.closing.cta.to"
          >
            {{ home.closing.cta.label }}
          </SfgButton>
        </div>
      </div>
    </SfgSheet>
  </div>
</template>
